import type { CommunityDetail } from '@pba/react-network';
import type { UserDetailConsent } from 'models';
import { type ReactNode, useMemo } from 'react';
import { Button, Card, Icon } from 'ui';

type P = {
  community: CommunityDetail;
  isLoading: boolean;
  isRequestPending: boolean;
  onJoin: () => void;
  onOpenConsentsModal: () => void;
  onOpenRequestJoinModal: () => void;
  usersConsents: UserDetailConsent[];
};

const JoinButton = ({
  community,
  isLoading,
  isRequestPending,
  onJoin,
  onOpenConsentsModal,
  onOpenRequestJoinModal,
  usersConsents,
}: P) => {
  const joinButton: {
    icon: ReactNode;
    onClick?: () => void;
    statusText: string;
    type: 'primary' | 'tertiary';
  } = useMemo(() => {
    if (
      community.organization.requiredConsents.some(
        (requiredConsent) => !usersConsents.some((consent) => requiredConsent.id === consent.id),
      )
    ) {
      return {
        icon: <Icon icon="enter-line" />,
        onClick: onOpenConsentsModal,
        statusText: 'Confirm consents to join',
        type: 'primary',
      };
    }

    if (isRequestPending) {
      return {
        icon: <Icon icon="check-circle-line" />,
        statusText: 'Request sent',
        type: 'tertiary',
      };
    }

    if (community.privacy === 'private') {
      return {
        icon: <Icon icon="add-circle-line" />,
        onClick: onOpenRequestJoinModal,
        statusText: 'Request to Join',
        type: 'primary',
      };
    }

    return {
      icon: <Icon icon="enter-line" />,
      onClick: onJoin,
      statusText: 'Join',
      type: 'primary',
    };
  }, [community, isRequestPending, onJoin, onOpenConsentsModal, onOpenRequestJoinModal, usersConsents]);

  return (
    <Card>
      <Button
        block
        disabled={!joinButton.onClick}
        icon={joinButton.icon}
        loading={isLoading}
        onClick={() => {
          joinButton.onClick?.();
        }}
        type={joinButton.type}
      >
        {joinButton.statusText}
      </Button>
    </Card>
  );
};

export default JoinButton;
